import React, { useEffect, useState, useCallback } from 'react';
import {
  UsersIcon,
  ClipboardDocumentListIcon,
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  StarIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { adminApi } from '../../services/adminApi';
import type { DashboardSummary } from '../../services/adminApi';
import { Link } from 'react-router-dom';

// Solução alternativa: Definindo tipos aqui para contornar problemas de importação
export interface QuickFeedbackSummary {
  total_count: number;
  count_by_score: Record<string, number>;
  last_submitted: string;
}

export interface DetailedFeedbackSummary {
	id: string;
	user_id: string;
	assessment_id: string;
	rating: number;
	comment: string;
	category: string;
	created_at: string;
	metadata: {
		average_accuracy_rating: number;
		average_overall_rating: number;
		average_satisfaction: number;
		recommendation_rate: number;
		section_ratings: Record<string, number>;
		total_feedbacks: number;
	};
}

interface DashboardStats {
  total_users: number;
  active_users: number;
  new_users_today: number;
  new_users_this_week: number;
  new_users_this_month: number;
  total_assessments: number;
  completed_assessments: number;
  assessments_today: number;
  total_feedbacks: number;
  average_feedback_rating: number;
  users_by_tier: Record<string, number>;
  assessments_by_status: Record<string, number>;
  feedbacks_by_rating: Record<string, number>;
}

interface StatCard {
  name: string;
  value: string;
  change: string;
  changeType: 'increase' | 'decrease' | 'neutral';
  icon: React.ComponentType<any>;
}

export default function AdminDashboard() {
  const [dashboardData, setDashboardData] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔄 AdminDashboard: Carregando dados do dashboard...');
      
      const data = await adminApi.getDashboardSummary();
      
      setDashboardData(data);
      console.log('✅ AdminDashboard: Dados carregados com sucesso');

    } catch (err) {
      setError('Erro ao carregar dados do dashboard');
      console.error('❌ Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  }, []); // Sem dependências para evitar loops

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-64 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow p-6">
                <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                <div className="h-8 bg-gray-200 rounded w-16 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-20"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <div className="text-sm text-red-700">{error}</div>
        <button
          onClick={loadDashboardData}
          className="mt-2 text-sm text-red-800 underline hover:text-red-900"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="rounded-md bg-yellow-50 p-4">
        <div className="text-sm text-yellow-700">
          Carregando dados do dashboard...
        </div>
      </div>
    );
  }

  // Criar cards de estatísticas a partir dos dados do dashboard
  const statCards: StatCard[] = dashboardData ? [
    {
      name: 'Total de Usuários',
      value: dashboardData.users.total_users.toString(),
      change: `+${dashboardData.users.new_users_30d} últimos 30 dias`,
      changeType: dashboardData.users.new_users_30d > 0 ? 'increase' : 'neutral',
      icon: UsersIcon,
    },
    {
      name: 'Usuários Ativos',
      value: dashboardData.users.active_users_30d.toString(),
      change: `${Math.round((dashboardData.users.active_users_30d / Math.max(dashboardData.users.total_users, 1)) * 100)}% do total`,
      changeType: 'neutral',
      icon: UsersIcon,
    },
    {
      name: 'Avaliações',
      value: dashboardData.assessments.total_assessments.toString(),
      change: `${dashboardData.assessments.completion_rate.toFixed(1)}% concluídas`,
      changeType: dashboardData.assessments.completion_rate > 70 ? 'increase' : dashboardData.assessments.completion_rate > 50 ? 'neutral' : 'decrease',
      icon: ClipboardDocumentListIcon,
    },
    {
      name: 'Rating Médio',
      value: dashboardData.feedbacks.average_rating.toFixed(1),
      change: `${dashboardData.feedbacks.total_feedbacks} feedbacks`,
      changeType: dashboardData.feedbacks.average_rating >= 4.0 ? 'increase' : dashboardData.feedbacks.average_rating >= 3.0 ? 'neutral' : 'decrease',
      icon: StarIcon,
    },
  ] : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard Administrativo</h1>
        <p className="mt-1 text-sm text-gray-500">
          Visão geral do sistema e métricas principais
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => (
          <div key={stat.name} className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <stat.icon className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {stat.name}
                  </dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">
                      {stat.value}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
            <div className="mt-4">
              <div className="flex items-center text-sm">
                {stat.changeType === 'increase' && (
                  <ArrowTrendingUpIcon className="h-4 w-4 text-green-500 mr-1" />
                )}
                {stat.changeType === 'decrease' && (
                  <ArrowTrendingDownIcon className="h-4 w-4 text-red-500 mr-1" />
                )}
                <span
                  className={
                    stat.changeType === 'increase'
                      ? 'text-green-700'
                      : stat.changeType === 'decrease'
                      ? 'text-red-700'
                      : 'text-gray-700'
                  }
                >
                  {stat.change}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Usuários por Tier */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Usuários por Plano
          </h3>
          <div className="space-y-3">
            {dashboardData ? Object.entries(dashboardData.users.users_by_tier).map(([tier, count]) => {
              const percentage = dashboardData.users.total_users > 0 ? (count / dashboardData.users.total_users) * 100 : 0;
              return (
                <div key={tier} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="text-sm font-medium text-gray-700 capitalize">
                      {tier === 'free' && 'Gratuito'}
                      {tier === 'premium' && 'Premium'}
                      {tier === 'mentoring' && 'Mentoria'}
                      {tier === 'enterprise' && 'Empresarial'}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="text-sm text-gray-500">
                      {count} ({percentage.toFixed(1)}%)
                    </div>
                    <div className="w-24 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              );
            }) : null}
          </div>
        </div>

        {/* Status das Avaliações */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Status das Avaliações
          </h3>
          <div className="space-y-3">
            {dashboardData ? Object.entries(dashboardData.assessments.assessments_by_status).map(([status, count]) => {
              const percentage = dashboardData.assessments.total_assessments > 0 ? (count / dashboardData.assessments.total_assessments) * 100 : 0;
              return (
                <div key={status} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="text-sm font-medium text-gray-700 capitalize">
                      {status === 'in_progress' && 'Em Progresso'}
                      {status === 'completed' && 'Concluídas'}
                      {status === 'abandoned' && 'Abandonadas'}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="text-sm text-gray-500">
                      {count} ({percentage.toFixed(1)}%)
                    </div>
                    <div className="w-24 bg-gray-200 rounded-full h-2">
                      <div
                                                className={`h-2 rounded-full ${
                          status === 'completed' ? 'bg-green-600' : 
                          status === 'in_progress' ? 'bg-yellow-600' : 'bg-red-600'
                        }`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              );
            }) : null}
          </div>
        </div>

        {/* Distribuição de Ratings */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Distribuição de Avaliações
          </h3>
          <div className="space-y-3">
            {dashboardData ? dashboardData.feedbacks.rating_distribution
              .sort((a, b) => b.rating - a.rating) // Sort from 5 to 1
              .map((item) => (
                <div key={item.rating} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="text-sm font-medium text-gray-700 flex items-center">
                      {item.rating} <StarIcon className="h-4 w-4 text-yellow-400 ml-1" />
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="text-sm text-gray-500">
                      {item.count} ({item.percentage.toFixed(1)}%)
                    </div>
                    <div className="w-24 bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          item.rating >= 4 ? 'bg-green-600' :
                          item.rating >= 3 ? 'bg-yellow-600' : 'bg-red-600'
                        }`}
                        style={{ width: `${item.percentage}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              )) : null}
          </div>
        </div>

        {/* Feedbacks Recentes */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                Feedbacks Recentes
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Últimos comentários dos usuários.
              </p>
            </div>
            <Link to="/admin/feedbacks" className="flex items-center text-sm font-medium text-blue-600 hover:text-blue-800">
              Ver Todos
              <MagnifyingGlassIcon className="ml-1 h-4 w-4" />
            </Link>
          </div>
          {dashboardData && dashboardData.feedbacks.recent_feedbacks.length > 0 ? (
            <div className="mt-4 space-y-4">
              {dashboardData.feedbacks.recent_feedbacks.map((feedback) => (
                <div key={feedback.id} className="border-l-4 border-blue-500 pl-4 py-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-900">{feedback.user_name}</span>
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => (
                        <StarIcon
                          key={i}
                          className={`h-4 w-4 ${
                            i < feedback.rating ? 'text-yellow-400' : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{feedback.comment}</p>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>{new Date(feedback.created_at).toLocaleDateString()}</span>
                    <span className="capitalize">{feedback.assessment_type}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 mt-4">Nenhum feedback recente encontrado.</p>
          )}
        </div>

        {/* Ações Rápidas */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Ações Rápidas
          </h3>
          <div className="space-y-3">
            <button className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <div className="flex items-center">
                <UsersIcon className="h-5 w-5 text-blue-500 mr-3" />
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    Gerenciar Usuários
                  </div>
                  <div className="text-xs text-gray-500">
                    Visualizar e editar usuários
                  </div>
                </div>
              </div>
            </button>
            
            <button className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <div className="flex items-center">
                <ChartBarIcon className="h-5 w-5 text-green-500 mr-3" />
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    Ver Relatórios
                  </div>
                  <div className="text-xs text-gray-500">
                    Analytics detalhados
                  </div>
                </div>
              </div>
            </button>
            
            <button className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <div className="flex items-center">
                <ChatBubbleLeftRightIcon className="h-5 w-5 text-purple-500 mr-3" />
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    Analisar Feedbacks
                  </div>
                  <div className="text-xs text-gray-500">
                    Comentários dos usuários
                  </div>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}